"use client";

import * as React from "react";
import { CheckCircle2, ChevronDown } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";

import { submitLead } from "../api/actions";
import { leadObjectTypes } from "../model/schema";
import type { LeadFormState } from "../model/types";

type LeadFieldErrors = NonNullable<LeadFormState["fieldErrors"]>;

const INITIAL_STATE: LeadFormState = { status: "idle" };

/** Стили нативного select — повторяют Input (aria-invalid уже учтён в Input). */
const selectClassName =
  "h-9 w-full cursor-pointer appearance-none rounded-lg border border-input bg-transparent py-1 pl-3 pr-9 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&>option]:bg-background";

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p role="alert" className="text-xs text-destructive">
      {message}
    </p>
  );
}

function LeadFormInner({ onRestart }: { onRestart: () => void }) {
  const [state, action, isPending] = React.useActionState(
    submitLead,
    INITIAL_STATE,
  );

  const fieldErrors: LeadFieldErrors | undefined =
    state.status === "error" ? state.fieldErrors : undefined;

  if (state.status === "success") {
    return (
      <div
        role="status"
        className="glass edge-light rounded-2xl p-8 text-center"
      >
        <CheckCircle2 aria-hidden className="mx-auto size-10 text-primary" />
        <p className="mt-4 text-base leading-relaxed text-foreground">
          {state.message}
        </p>
        <Button variant="outline" size="lg" className="mt-6" onClick={onRestart}>
          Отправить ещё
        </Button>
      </div>
    );
  }

  return (
    <form action={action} noValidate className="flex flex-col gap-4">
      {state.status === "error" && state.message ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive"
        >
          {state.message}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lead-name">Имя</Label>
        <Input
          id="lead-name"
          name="name"
          autoComplete="name"
          placeholder="Как к вам обращаться"
          aria-invalid={fieldErrors?.name ? true : undefined}
        />
        <FieldError message={fieldErrors?.name} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lead-phone">Телефон</Label>
        <Input
          id="lead-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          placeholder="+7 999 123-45-67"
          aria-invalid={fieldErrors?.phone ? true : undefined}
        />
        <FieldError message={fieldErrors?.phone} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lead-email">
          Email
          <span className="text-xs font-normal text-muted-foreground">
            — необязательно
          </span>
        </Label>
        <Input
          id="lead-email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.ru"
          aria-invalid={fieldErrors?.email ? true : undefined}
        />
        <FieldError message={fieldErrors?.email} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lead-object-type">Тип объекта</Label>
        <div className="relative">
          <select
            id="lead-object-type"
            name="objectType"
            className={selectClassName}
            defaultValue={leadObjectTypes[0]}
            aria-invalid={fieldErrors?.objectType ? true : undefined}
          >
            {leadObjectTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
        </div>
        <FieldError message={fieldErrors?.objectType} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lead-message">
          Комментарий
          <span className="text-xs font-normal text-muted-foreground">
            — необязательно
          </span>
        </Label>
        <Textarea
          id="lead-message"
          name="message"
          rows={3}
          placeholder="Площадь, город, сроки — в свободной форме"
          aria-invalid={fieldErrors?.message ? true : undefined}
        />
        <FieldError message={fieldErrors?.message} />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-start gap-2.5">
          <input
            id="consent"
            name="consent"
            type="checkbox"
            aria-invalid={fieldErrors?.consent ? true : undefined}
            className="mt-0.5 size-4 shrink-0 cursor-pointer rounded-sm border-input accent-[var(--primary)] outline-none focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive"
          />
          <Label
            htmlFor="consent"
            className="text-xs leading-relaxed font-normal text-muted-foreground"
          >
            Согласен на обработку персональных данных
          </Label>
        </div>
        <FieldError message={fieldErrors?.consent} />
      </div>

      <Button type="submit" size="lg" className="w-full" disabled={isPending}>
        {isPending ? "Отправляем…" : "Отправить заявку"}
      </Button>
    </form>
  );
}

/**
 * Публичный компонент: держит ключ монтирования, чтобы «Отправить ещё»
 * сбрасывала useActionState к начальному состоянию через ремоут формы.
 */
export function LeadForm() {
  const [formKey, setFormKey] = React.useState(0);

  return (
    <LeadFormInner
      key={formKey}
      onRestart={() => setFormKey((key) => key + 1)}
    />
  );
}
